// The draw tool (issue 1032, design/28 §4): the board mode in which a drag draws a stroke, a line, a rectangle or an
// ellipse, a click places text, and in Erase a click removes one's own drawing. One board mode at a time: it stops
// pointing, measuring, laying an area and the fog, and each of them stops it. Held outside React, like the others; the
// toolbar and the canvas both read it. Nothing here touches the socket.
import type { Box, DrawKind, DrawWidth, Stamp, TextSize } from '@dragons-table/vtt-shared';
import { wallTool } from './wallTool';
import { focusTool } from './focusTool';
import { measureTool } from './measureTool';
import { targetTool } from './targetTool';
import { areaTool } from './areaTool';
import { fogTool } from './fogTool';
import { zoneTool } from './zoneTool';
import { whatsThisTool } from './whatsThisTool';

/** `stampBox`: Save as stamp, a drag draws the box (issue 1034). `stamp`: each click puts down the picked stamp. */
export type DrawMode = DrawKind | 'erase' | 'stampBox' | 'stamp';

/** The colours after the first, which is the person's own identity colour (design/28 §1). */
export const DRAW_SWATCHES = ['#ef4444', '#3b82f6', '#22c55e', '#fafaf9', '#0c0a09'] as const;

export interface DrawDraft {
  on: boolean;
  mode: DrawMode;
  /** '' until chosen: the person's own colour. */
  colour: string;
  width: DrawWidth;
  size: TextSize;
  /** The DM's planning layer (issue 1033): what the DM draws now only the DM sees. Never on for anyone else. */
  planning: boolean;
  /** Text being typed: where it goes on the board, and where that is on the screen (relative to the board's host). */
  text: { x: number; y: number; sx: number; sy: number } | null;
  /** Save as stamp (issue 1034): the box drawn around one's marks, waiting for a name. Only in `stampBox`. */
  box: Box | null;
  /** The stamp picked from one's library: each click puts a copy down. Only in `stamp`; it stays until another tool. */
  stamp: Stamp | null;
}

type Listener = (d: DrawDraft) => void;
const initial = (keep?: DrawDraft): DrawDraft => ({ on: false, mode: keep?.mode === 'stamp' || keep?.mode === 'stampBox' ? 'stroke' : keep?.mode ?? 'stroke', colour: keep?.colour ?? '', width: keep?.width ?? 4, size: keep?.size ?? 24, planning: keep?.planning ?? false, text: null, box: null, stamp: null });
let draft: DrawDraft = initial();
const listeners = new Set<Listener>();
const set = (next: DrawDraft) => { draft = next; for (const l of listeners) l(draft); };

export const drawTool = {
  get: () => draft,
  subscribe(listener: Listener): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; },
  start(): void { whatsThisTool.stop(); focusTool.stop(); measureTool.stop(); areaTool.stop(); targetTool.stop(); fogTool.stop(); wallTool.stop(); zoneTool.stop(); set({ ...initial(draft), on: true }); },
  /** Off; the choices of kind, colour and width are kept for next time. */
  stop(): void { if (draft.on) set(initial(draft)); },
  toggle(): void { if (draft.on) drawTool.stop(); else drawTool.start(); },
  change(patch: Partial<Omit<DrawDraft, 'on' | 'text' | 'box' | 'stamp'>>): void {
    if (!draft.on) return;
    const mode = patch.mode ?? draft.mode;
    set({ ...draft, ...patch, text: mode === 'text' ? draft.text : null, box: mode === 'stampBox' ? draft.box : null, stamp: mode === 'stamp' ? draft.stamp : null });
  },
  placeText(at: DrawDraft['text']): void { if (draft.on) set({ ...draft, text: at }); },
  setBox(box: Box | null): void { if (draft.on && draft.mode === 'stampBox') set({ ...draft, box }); },
  /** Pick a stamp (starting the tool if it is off): the board's clicks put it down until another tool is chosen. */
  pickStamp(stamp: Stamp | null): void {
    if (!draft.on) drawTool.start();
    set({ ...draft, mode: stamp ? 'stamp' : draft.mode === 'stamp' ? 'stroke' : draft.mode, stamp, box: null, text: null });
  },
};
